// Aperçu d'une version telle que l'usager la verra : rendu du serveur (GET .../apercu : identité,
// conditions, pièces, étapes, délais, tarif marqué fictif, textes) et formulaire interactif rendu
// avec le moteur partagé pour vérifier les conditions d'affichage et les validations.
import { schemaFormulaire, STATUTS_USAGER, type DonneesFormulaire, type Formulaire } from '@cc/contrats';
import { Bandeau, Bouton, LienBouton, Onglets, Panneau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { RenduFormulaire, validerSaisie, valeursInitiales } from '../../formulaire/RenduFormulaire';
import { LIBELLES_NATURE_ETAPE, useVersion } from './contenu';
import commun from '../../commun/commun.module.css';
import styles from './demarches.module.css';

interface Apercu {
  demarche: { id: string; code: string; libelle: string };
  version: { numero: number; etat: string; publiee_le: string | null };
  identite: { libelle: string; description: string; domaine: string; autorite_responsable: string; fondement: string };
  conditions_eligibilite: { texte: string; regles: { message: string }[] };
  identite_verifiee_requise: boolean;
  sections: { code: string; titre: string; description: string | null; conditionnelle: boolean; visible_initialement: boolean; champs: { code: string; type_libelle: string; libelle: string; aide: string | null; obligatoire: boolean; conditionnel: boolean; visible_initialement: boolean; calcule: boolean }[] }[];
  pieces: { type: string; libelle: string; obligatoire: boolean; conditionnelle: boolean; aide: string | null }[];
  etapes: { rang: number; code: string; libelle: string; nature: string; service: { id: string; nom: string | null } | null; delai_interne_jours: number | null }[];
  delais: { reglementaire: { libelle: string } | null; interne: { libelle: string } | null; relance_complement_jours: number; classement_sans_suite_jours: number };
  tarif: { libelle: string; mention: string | null; rubrique: string | null; montant: number | null };
  canaux_notification: string[];
  textes_statuts: { statut: string; libelle: string; texte: string | null }[];
}

function Essai({ formulaire, tarifs }: { formulaire: Formulaire; tarifs: Record<string, number> }) {
  const [valeurs, setValeurs] = useState<DonneesFormulaire>(() => valeursInitiales(formulaire));
  const [resultat, setResultat] = useState<ReturnType<typeof validerSaisie> | null>(null);
  return (
    <div className={commun.pile}>
      <p className={commun.na}>Saisie d'essai : rien n'est enregistré. Les champs conditionnels apparaissent selon vos réponses ; « Vérifier » applique les règles du moteur, identiques à celles du serveur.</p>
      <RenduFormulaire formulaire={formulaire} valeurs={valeurs} onChanger={setValeurs} erreurs={resultat?.erreurs ?? []} tarifs={tarifs} />
      <div className={commun.ligne}>
        <Bouton variante="secondaire" onClick={() => setResultat(validerSaisie(formulaire, valeurs, { tarifs }))}>
          Vérifier la saisie
        </Bouton>
        <Bouton variante="discret" onClick={() => { setValeurs(valeursInitiales(formulaire)); setResultat(null); }}>
          Réinitialiser
        </Bouton>
      </div>
      {resultat ? (
        resultat.valide ? (
          <Bandeau type="succes">Saisie valide : {Object.keys(resultat.donnees).length} valeur(s) retenue(s){Object.entries(resultat.donnees).filter(([k]) => formulaire.sections.some((s) => s.champs.some((c) => c.code === k && c.calcul))).map(([k, v]) => `, ${k} = ${String(v)}`)}.</Bandeau>
        ) : (
          <Bandeau type="erreur">{resultat.erreurs.length} erreur(s) : {resultat.erreurs.map((e) => `${e.champ} (${e.message})`).join(' ; ')}</Bandeau>
        )
      ) : null}
    </div>
  );
}

export function PageApercuVersion() {
  const { id = '', v = '' } = useParams();
  const numero = Number(v);
  const apercu = useQuery({
    queryKey: ['demarches', 'apercu', id, numero],
    queryFn: ({ signal }) => requete<Apercu>(`/demarches/${id}/versions/${numero}/apercu`, { signal }),
    retry: false,
  });
  const version = useVersion(id, numero);
  const formulaire = useMemo(() => {
    const r = schemaFormulaire.safeParse((version.data?.contenu as { formulaire?: unknown })?.formulaire);
    return r.success ? r.data : null;
  }, [version.data]);
  const a = apercu.data;
  const tarifs = a?.tarif.rubrique && typeof a.tarif.montant === 'number' ? { [a.tarif.rubrique]: a.tarif.montant } : {};

  return (
    <>
      <EnTetePage
        etiquette={a ? `Démarches / ${a.demarche.libelle} / Version ${a.version.numero}` : 'Aperçu'}
        titre={a ? `Aperçu usager : ${a.identite.libelle}` : 'Aperçu usager'}
        description="Ce que l'usager verra dans le catalogue et au dépôt. Hors production, le tarif porte la mention « tarif fictif de présentation »."
        actions={<LienBouton href={`/demarches/${id}/versions/${numero}`} icone={ArrowLeft}>Retour à l'éditeur</LienBouton>}
      />
      <EtatRequete chargement={apercu.isPending} erreur={apercu.error} onReessayer={() => void apercu.refetch()} lot="L1-A" fonction="L'aperçu de la version" lignes={8}>
        {a ? (
          <Onglets
            libelle="Aperçu"
            onglets={[
              {
                id: 'fiche',
                libelle: 'Fiche de la démarche',
                contenu: (
                  <div className={commun.deuxColonnes}>
                    <div className={commun.pile}>
                      <Panneau titre="À quoi sert cette démarche">
                        <p>{a.identite.description || 'Aucune description.'}</p>
                        <dl className={commun.definitions}>
                          <dt>Autorité</dt>
                          <dd>{a.identite.autorite_responsable}</dd>
                          <dt>Fondement</dt>
                          <dd>{a.identite.fondement}</dd>
                          <dt>Identité vérifiée</dt>
                          <dd>{a.identite_verifiee_requise ? 'Requise (pièce vue au guichet)' : 'Non requise'}</dd>
                        </dl>
                      </Panneau>
                      <Panneau titre="Qui peut demander">
                        <p>{a.conditions_eligibilite.texte || 'Aucune condition particulière.'}</p>
                        {a.conditions_eligibilite.regles.length > 0 ? (
                          <ul>
                            {a.conditions_eligibilite.regles.map((r, i) => (
                              <li key={i}>{r.message}</li>
                            ))}
                          </ul>
                        ) : null}
                      </Panneau>
                      <Panneau titre={`Pièces à fournir (${a.pieces.length})`}>
                        {a.pieces.length === 0 ? <p>Aucune pièce demandée.</p> : null}
                        <ul>
                          {a.pieces.map((p) => (
                            <li key={p.type}>
                              {p.libelle} : {p.obligatoire ? 'obligatoire' : 'facultative'}
                              {p.conditionnelle ? ', selon votre situation' : ''}
                              {p.aide ? <span className={commun.na}> ({p.aide})</span> : null}
                            </li>
                          ))}
                        </ul>
                      </Panneau>
                    </div>
                    <div className={commun.pile}>
                      <Panneau titre="Délais et coût">
                        <dl className={commun.definitions}>
                          <dt>Délai de réponse</dt>
                          <dd>{a.delais.reglementaire?.libelle ?? 'non renseigné'}</dd>
                          <dt>Objectif interne</dt>
                          <dd>{a.delais.interne?.libelle ?? 'non renseigné'}</dd>
                          <dt>Coût</dt>
                          <dd>{a.tarif.libelle}</dd>
                          <dt>Information</dt>
                          <dd>{a.canaux_notification.join(', ')}</dd>
                        </dl>
                      </Panneau>
                      <Panneau titre="Étapes expliquées">
                        <ol>
                          {a.etapes.map((e) => (
                            <li key={e.code}>
                              {e.libelle} ({LIBELLES_NATURE_ETAPE[e.nature] ?? e.nature}
                              {e.service?.nom ? `, ${e.service.nom}` : ''}
                              {e.delai_interne_jours ? `, ${e.delai_interne_jours} jours` : ''})
                            </li>
                          ))}
                        </ol>
                      </Panneau>
                      <Panneau titre="Textes par statut">
                        <dl className={styles.textes}>
                          {a.textes_statuts.map((t) => (
                            <div key={t.statut}>
                              <dt>{STATUTS_USAGER[t.statut as keyof typeof STATUTS_USAGER] ?? t.libelle}</dt>
                              <dd className={t.texte ? undefined : commun.na}>{t.texte ?? 'texte par défaut de la plateforme'}</dd>
                            </div>
                          ))}
                        </dl>
                      </Panneau>
                    </div>
                  </div>
                ),
              },
              {
                id: 'formulaire',
                libelle: 'Formulaire tel que rempli',
                contenu: formulaire ? <Essai formulaire={formulaire} tarifs={tarifs} /> : <p className={commun.na}>Le formulaire de cette version n'est pas au format de l'éditeur (démarche interne de test) : aucun rendu interactif.</p>,
              },
              {
                id: 'structure',
                libelle: 'Structure du formulaire',
                contenu: (
                  <div className={commun.pile}>
                    {a.sections.map((s) => (
                      <Panneau key={s.code} titre={`${s.titre}${s.conditionnelle ? ' (section conditionnelle)' : ''}`} niveau={3}>
                        {s.champs.map((c) => (
                          <div key={c.code} className={styles.apercuChamp}>
                            <span>
                              {c.libelle}
                              {c.obligatoire ? ' *' : ''}
                              {c.aide ? <small> : {c.aide}</small> : null}
                            </span>
                            <small>
                              {c.type_libelle}
                              {c.conditionnel ? `, ${c.visible_initialement ? 'visible au départ' : 'masqué au départ'}` : ''}
                              {c.calcule ? ', calculé' : ''}
                            </small>
                          </div>
                        ))}
                      </Panneau>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        ) : null}
      </EtatRequete>
    </>
  );
}
